/* eslint-disable import/no-duplicates */
import { apiCall, ApiParams, ApiResps } from 'providers';
import { GetGoalsResponse, GoalTableItem, Option } from './types';
import i18n from 'i18n';
import { format, parseISO } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { capitalizeFirstLetter } from 'helpers/capitalizeFirstLetter';
import { UnitStatusKey } from 'providers/types/unit';
import {
  ConsumptionFormValue,
  FormValues,
  MonthKey,
} from './components/Form/types';
import { months, monthToNumber } from './components/Form/constants';

export function getYearOptionsByGoals(unitGoals: GetGoalsResponse, year: string): Option[] {
  const options = [{
    label: year,
    value: year,
  }];

  const goalYears = unitGoals.filter((goal) => goal.YEAR !== Number(year)).map((item) => item.YEAR);

  const allYears = new Set(goalYears);

  for (const year of allYears) {
    options.push({
      label: year.toString(),
      value: year.toString(),
    });
  }

  return options.sort((optionA, optionB) => Number(optionA.value) - Number(optionB.value));
}

export function getLastUpdatedAtGoal(goals: GetGoalsResponse[number]['GOALS']): string {
  const lastGoal = [...goals].sort((a, b) => parseISO(b.UPDATED_AT).getTime() - parseISO(a.UPDATED_AT).getTime())[0];

  return capitalizeFirstLetter(format(parseISO(lastGoal.UPDATED_AT), 'MMM/y', {
    locale: i18n.language === 'pt' ? ptBR : enUS,
  }));
}

export function parseEnergyGoalsDataTable(unitGoals: GetGoalsResponse, currentYear: string, searchValue: string): GoalTableItem[] {
  const data: GoalTableItem[] = [];

  const unitGoalsYear = unitGoals.filter((item) => item.YEAR
    === Number(currentYear));

  for (const unitGoal of unitGoalsYear) {
    if (searchValue && !unitGoal.UNIT_NAME.toLowerCase().includes(searchValue)) continue;

    data.push({
      UNIT_ID: unitGoal.UNIT_ID,
      UNIT_NAME: unitGoal.UNIT_NAME,
      UPDATED_AT: getLastUpdatedAtGoal(unitGoal.GOALS),
      YEAR: Number(currentYear),
      GOALS: unitGoal.GOALS,
    });
  }

  return data;
}

export async function getUnitList(clientId: number, unitStatus?: UnitStatusKey[]): Promise<{ UNIT_ID: number, UNIT_NAME: string }[]> {
  try {
    const response = await apiCall('/clients/get-units-list-basic', {
      CLIENT_ID: clientId,
      INCLUDE_UNIT_STATUS: unitStatus,
    });

    return response.list.map((item) => ({
      UNIT_ID: item.UNIT_ID,
      UNIT_NAME: item.UNIT_NAME,
    }));
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function setEnergyGoals(reqParams: ApiParams['/energy/set-goals']): Promise<ApiResps['/energy/set-goals'] | undefined> {
  try {
    const response = await apiCall('/energy/set-goals', reqParams);

    return response;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export function prepareSetGoalsParams(data: FormValues, unitId: string, defaultValues?: FormValues): ApiParams['/energy/set-goals'][] {
  const paramsByYear: ApiParams['/energy/set-goals'][] = [];

  for (const [yearKey, values] of Object.entries(data)) {
    const goals = prepareGoalInput(values, defaultValues?.[yearKey]);

    if (goals.length === 0) continue;

    const request: ApiParams['/energy/set-goals'] = {
      UNIT_ID: Number(unitId),
      YEAR: Number(yearKey),
      GOALS: goals,
    };

    paramsByYear.push(request);
  }

  return paramsByYear;
}

function prepareGoalInput(formValue: ConsumptionFormValue, defaultValues?: ConsumptionFormValue): ApiParams['/energy/set-goals']['GOALS'] {
  const goals: ApiParams['/energy/set-goals']['GOALS'] = [];

  for (const [keyMonth, { value, past }] of Object.entries(formValue)) {
    if (past) continue;

    const operation = handleOperationByMonth(value, defaultValues?.[keyMonth]);
    if (!operation) continue;

    const month = monthToNumber(keyMonth as typeof months[number]);
    if (month < 0) continue;

    goals.push({
      MONTH: month,
      VALUE_KWH: formatValueKwh(value ?? '0'),
      ...operation,
    });
  }

  return goals;
}

function handleOperationByMonth(value: string, defaultValues?: ConsumptionFormValue[MonthKey]): Pick<ApiParams['/energy/set-goals']['GOALS'][number], 'DELETE' | 'EDIT' | 'INSERT' | 'GOAL_ID'> | undefined {
  if (!defaultValues && !value) {
    return undefined;
  }

  if ((!defaultValues?.value && !!value) || (defaultValues?.past && !!value)) {
    return {
      INSERT: true,
    };
  }

  if (defaultValues?.value && !!value && defaultValues.value !== value) {
    return {
      GOAL_ID: Number(defaultValues.id as string),
      EDIT: true,
    };
  }

  if (defaultValues?.value && !value) {
    return {
      GOAL_ID: Number(defaultValues.id),
      DELETE: true,
    };
  }

  return undefined;
}

function formatValueKwh(value: string): number {
  const formattedValue = i18n.language === 'pt'
    ? value.replaceAll('.', '').replaceAll(',', '.')
    : value.replaceAll(',', '');

  return Number(formattedValue);
}

export function prepareDeleteGoalsParams(unitId: number, year: number, goals: GetGoalsResponse[number]['GOALS']): ApiParams['/energy/set-goals'] {
  const goalsInput: ApiParams['/energy/set-goals']['GOALS'] = [];

  for (const goal of goals) {
    goalsInput.push({
      GOAL_ID: goal.GOAL_ID,
      MONTH: goal.MONTH,
      VALUE_KWH: 0,
      DELETE: true,
    });
  }

  return {
    UNIT_ID: unitId,
    YEAR: year,
    GOALS: goalsInput,
  };
}
